import { useMemo } from 'react';
import { Paper, Typography, Box, makeStyles, Divider, Grid } from '@material-ui/core';
import { TicketIcon } from '../../icons';

const useStyles = makeStyles(theme => ({
    root: props => ({
        padding: theme.spacing(2),
        borderBottom: '1px solid #eee',
        borderLeft: `4px solid ${props.barColor || theme.palette.primary.main}`,
        backgroundColor: theme.palette.grey[50],
        borderRadius: theme.shape.borderRadius,
        marginBottom: theme.spacing(3)
    }),
    headerRow: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(1.5)
    },
    ticketIcon: {
        marginRight: theme.spacing(1),
        fontSize: 20,
        color: theme.palette.primary.main
    },
    title: {
        fontWeight: 700,
        fontSize: 15
    },
    guestCard: {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        backgroundColor: '#fff',
        borderRadius: theme.shape.borderRadius
    },
    guestTitle: {
        fontWeight: 600,
        fontSize: 14,
        marginBottom: theme.spacing(1.5),
        color: theme.palette.primary.main
    },
    fieldRow: {
        marginBottom: theme.spacing(1)
    },
    fieldLabel: {
        fontSize: 11,
        color: theme.palette.grey[500],
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 2
    },
    fieldValue: {
        fontSize: 14,
        color: theme.palette.text.primary,
        wordBreak: 'break-word'
    }
}));

// checks if its an object and its not empty
const isNonEmptyObject = value =>
    value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length > 0;

/**
 * Special rendering for object-type fields (hotel, lodging, etc.)
 * Object field types store data in a specific structure with a 'type' property
 * and a 'name' property for display purposes.
 *
 * Supported object field types:
 * - hotel: { type: 'hotel', hotel: { id, name, island_id, island }, name? }
 * - lodging: { type: 'lodging', name, lodging, lodging_room_number, self_lodging_address, route, stop }
 *
 * When adding new object field types, update this function and:
 * - GuestPageSidebar.js (guest_field_info display)
 * - convertBookingfieldsAndGuestdataToTicketInfo.js (backend processing)
 * - guest_fields.ejs (email template)
 * - FareharborDirectService.js (if FH integration needed)
 */
const formatFieldValue = value => {
    if (value == null || value === '') {
        return '-';
    }

    // Boolean values
    if (value === true) {
        return 'Yes';
    }
    if (value === false) {
        return 'No';
    }

    // Hotel type: display hotel name
    if (value?.type === 'hotel') {
        return value.hotel?.name ?? '-';
    }

    // Lodging type: display name property
    if (value?.type === 'lodging') {
        return value.name ?? '-';
    }

    // Generic object with name property
    if (typeof value === 'object' && value.name) {
        return value.name;
    }

    // Other objects - stringify
    if (typeof value === 'object') {
        return JSON.stringify(value);
    }

    return String(value);
};

const BookingFieldsTable = ({ title, guests, barColor }) => {
    const classes = useStyles({ barColor });

    // only non-empty objects and recompute only when changes
    const cleaned = useMemo(() => (guests || []).filter(isNonEmptyObject), [guests]);

    if (cleaned.length === 0) {
        return null;
    }

    return (
        <Box className={classes.root}>
            <Box className={classes.headerRow}>
                <TicketIcon className={classes.ticketIcon} />
                <Typography className={classes.title}>{title}</Typography>
            </Box>
            <Divider style={{ marginBottom: 12 }} />

            {cleaned.map((guest, guestIdx) => (
                <Paper key={guestIdx} className={classes.guestCard} elevation={1}>
                    {cleaned.length > 1 && (
                        <Typography className={classes.guestTitle}>
                            Guest #{guestIdx + 1}
                        </Typography>
                    )}
                    <Grid container spacing={2}>
                        {Object.entries(guest).map(([fieldName, fieldValue]) => (
                            <Grid item xs={12} sm={6} key={fieldName}>
                                <Box className={classes.fieldRow}>
                                    <Typography className={classes.fieldLabel}>
                                        {fieldName}
                                    </Typography>
                                    <Typography className={classes.fieldValue}>
                                        {formatFieldValue(fieldValue)}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>
            ))}
        </Box>
    );
};

export default BookingFieldsTable;
